import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import Tag from 'legacy-src/utility/Tag';
import { RESTRICT_CHILD_TAGS } from 'legacy-src/utility/restrictedTags';
import {
  recursiveTagDescDelta,
  determineFirstLevel,
  flatHierarchy,
  setTagSelections,
  selectTag,
  deselectTag,
  recursiveTagIndex,
  preToggleFilters,
} from 'legacy-src/utility/tagHelpers';
import ListContainerControls from '../Filters/components/ListContainerControls';
import DetailsPopover, {
  FILTER_DETAILS,
} from 'legacy-src/components/assessment/Filters/components/DetailsPopover';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import Button from 'chameleon/ui-stack/ui/base/button';
import { HIERARCHY_DEPENDEND_FILTERS } from 'chameleon-src/constants/subjects';
import {
  INCLUDE_FILTER,
  INCLUDE_ONLY_FILTER,
  EXCLUDE_HIGHER_FILTER,
} from 'chameleon-src/constants/filters';
import InclusionFilters from './InclusionFilters';
import { setMathJaxTypeset } from 'chameleon-src/utility/utils';

const NodeCheckBoxState = {
  UNSELECTED: 0,
  INDETERMINATE: 1,
  SELECTED: 2,
};

const Node = (props) => {
  const {
    tag,
    selectedFilters,
    selectType,
    isFullText,
    unselectTag,
    unselectTagWithChildrenAndParents,
    addTag,
    addTagAndChildren,
    tagCategory,
    showAttestationFlag,
    containerClassName,
    classNameLabel,
    level,
    collapsed,
    isCollapsible,
    enableHierarchicalSelection,
    flattenHierarchy,
  } = props;

  const { id: tagId, label, description } = tag;
  const inputType = selectType === 'single' ? 'radio' : 'checkbox';
  const mixedStateCheckbox =
    enableHierarchicalSelection && selectType !== 'single';

  const checkboxRef = useRef();
  const [checkboxState, setCheckboxState] = useState(
    NodeCheckBoxState.UNSELECTED,
  );

  useEffect(() => {
    // Don't try to do anything smart without the FF
    if (!mixedStateCheckbox) return;
    // The end nodes don't have a presence in the `flattenHierarchy`, for reasons unknown
    // But since they're leafs, they don't have to sub select any other tag, so we're fine using just their id
    const childrenIds = _.isObject(flattenHierarchy.current[tagId])
      ? flattenHierarchy.current[tagId].childrenIds
      : [tagId];
    const selectedChildren = _.intersection(selectedFilters, childrenIds);
    const currentTagSelected = selectedFilters.indexOf(tagId) > -1;

    // When nothing matches, the checkbox should not be selected
    if (selectedChildren.length === 0) {
      setCheckboxState(NodeCheckBoxState.UNSELECTED);
      checkboxRef.current.indeterminate = false;
      return;
    }

    // Whenever all sub-children of the current node are selected,
    // Force selection of the current node
    if (
      selectedChildren.length === childrenIds.length - 1 &&
      !currentTagSelected
    ) {
      addTag(tag, tagCategory.id, selectType);
      return;
    }

    // When the current tag and all its sub-children are selected,
    // show the state as selected
    if (selectedChildren.length === childrenIds.length) {
      setCheckboxState(NodeCheckBoxState.SELECTED);
      checkboxRef.current.indeterminate = false;
      return;
    }

    // When only the current node is selected *and* not any of its sub-children
    // Unselet it
    if (selectedChildren.length === 1 && selectedChildren[0] === tagId) {
      unselectTag(tag);
      checkboxRef.current.indeterminate = false;
    } else if (selectedChildren.length > 0) {
      // Even if one of current node sub-child is selected
      // Mark the checkbox as `indeterminate`
      setCheckboxState(NodeCheckBoxState.INDETERMINATE);
      checkboxRef.current.indeterminate = true;
    }
  }, [selectedFilters]);

  const onCheckboxChange = () => {
    switch (checkboxState) {
      // When the node is completely selected we have to completely deselect all it's children
      case NodeCheckBoxState.SELECTED:
        unselectTagWithChildrenAndParents(tag);
        break;
      case NodeCheckBoxState.INDETERMINATE:
        unselectTagWithChildrenAndParents(tag);
        break;
      case NodeCheckBoxState.UNSELECTED:
        addTagAndChildren(tag, tagCategory.id, selectType);
        break;
      default:
        break;
    }
  };

  const onSimpleChange = () => {
    if (_.includes(selectedFilters, tagId)) {
      unselectTag(tag);
    } else {
      addTag(tag, tagCategory.id, selectType);
    }
  };

  // When the FF is on use the `checkboxState`
  // When the FF is off use the old `isSelected` state
  const isSelected =
    (mixedStateCheckbox && checkboxState === NodeCheckBoxState.SELECTED) ||
    (!mixedStateCheckbox && _.indexOf(selectedFilters, tagId) !== -1);

  const onChange = mixedStateCheckbox ? onCheckboxChange : onSimpleChange;

  const childrenRef = useRef();
  const [isCollapsed, setCollapsed] = useState(true);
  const onCollapse = (event) => {
    event.stopPropagation();
    setCollapsed((prev) => !prev);
  };

  useLayoutEffect(() => {
    // Rerender MathJax if children of node were opened
    !isCollapsed &&
      childrenRef.current &&
      setMathJaxTypeset(childrenRef.current);
  }, [isCollapsed]);

  useEffect(() => {
    level <= 1 && setCollapsed(collapsed);
  }, [collapsed, level]);

  setTagsIfTagIsFFlag(tag, showAttestationFlag);

  const hasChildren = useMemo(() => (tag.tags || []).length > 0, [tag.tags]);

  return (
    <Fragment>
      <li key={tagId} data-test-filter-node>
        <div
          className={`${containerClassName} cursor-pointer flex`}
          onClick={onChange}
          role="button"
          tabIndex={-1}
        >
          <div
            className={cn('flex items-center', {
              'pl-8.4': !hasChildren && level === 2,
              'pl-8': !hasChildren && level > 1,
            })}
          >
            {isCollapsible && hasChildren && (
              <span className="">
                <ButtonWithIcon
                  isIcon
                  isNaked
                  size="tiny"
                  className="bg-none"
                  disabled={false}
                  onClick={onCollapse}
                  icon="dropdown_arrow"
                  iconClassName={cn({
                    'text-gray-800': true,
                    '-rotate-90': isCollapsed,
                    '-ml-0.3': level > 1,
                  })}
                  iconWidth={20}
                  iconHeight={20}
                />
              </span>
            )}
            <input
              ref={checkboxRef}
              className="TagTreeSelector--tag_item_input mr-4 cursor-pointer"
              type={inputType}
              onChange={onChange}
              checked={isSelected}
            />
            {isFullText ? (
              <span
                className={`TagTreeSelector--tag_item_name ${classNameLabel}`}
              >
                <div dangerouslySetInnerHTML={{ __html: description }} />
              </span>
            ) : (
              <span className={cn(classNameLabel)}>{label}</span>
            )}
          </div>
        </div>
      </li>
      {hasChildren && (
        <li
          ref={childrenRef}
          className={cn('pl-16 text-bold', {
            'pl-16 pr-6': level === 1,
            'pl-8.4 pr-0': level > 1,
            hidden: isCollapsible && isCollapsed,
          })}
        >
          <ul className="p-0 m-0 list-none">
            {/* Show children tags either if FF flag is on and the tag is expanded or when the FF is turned off */}
            {((isCollapsible && !isCollapsed) || !isCollapsible) &&
              tag.tags.map((childTag) => (
                <Node
                  key={childTag.id}
                  tag={childTag}
                  {..._.omit(props, ['key', 'tag', 'level'])}
                  containerClassName="py-0.8"
                  level={level + 1}
                  collapsed={isCollapsed}
                  classNameLabel="text-base text-gray-700"
                />
              ))}
          </ul>
        </li>
      )}
    </Fragment>
  );
};

Node.displayName = 'Node';

const setTagsIfTagIsFFlag = (tag, showAttestationFlag) => {
  if (
    !showAttestationFlag &&
    (RESTRICT_CHILD_TAGS.includes(tag.label) ||
      RESTRICT_CHILD_TAGS.includes(tag.description))
  ) {
    tag.tags = null;
  }
};

const getAllTagIds = (tags) => {
  if (_.isArray(tags)) {
    return tags.map(getAllTagIds);
  } else if (tags) {
    if (tags.tags) {
      return [tags.id].concat(...tags.tags.map(getAllTagIds));
    }
    return tags.id;
  }
};

const TagTreeSelector = (props) => {
  const {
    tagCategory,
    tagCategoryId,
    eliminateTags,
    inlineFilters,
    isFullTextToggleAvailable,
    isDisabled,
    filterCursor,
    flattenHierarchy,
    showNewExamAlignmentText,
    subjectId,
    enableInclusionFilters,
    includeOnlyIds,
    excludeHigherIds,
    onFilterChange,
    isCollapsible,
    enableHierarchicalSelection,
    className,
    selectType,
  } = props;

  const hasExcludeHigherFilter =
    enableInclusionFilters &&
    subjectId in HIERARCHY_DEPENDEND_FILTERS &&
    HIERARCHY_DEPENDEND_FILTERS[subjectId].includes(tagCategory.label);

  const updateCursor = (data) => {
    const newData = enableInclusionFilters
      ? data.tags[tagCategoryId]
      : data[tagCategoryId];
    setSelectedFilters(newData);
  };

  const updateInclusionFilterType = (data) => {
    const { excludeHigher, includeOnly } = data.inclusionFilters;
    const isExcludeHigher = excludeHigher.includes(tagCategoryId);
    const isIncludeOnly = includeOnly.includes(tagCategoryId);

    if (isIncludeOnly) {
      setSelectedInclusionFilterType(INCLUDE_ONLY_FILTER);
    } else if (isExcludeHigher) {
      setSelectedInclusionFilterType(EXCLUDE_HIGHER_FILTER);
    } else {
      setSelectedInclusionFilterType(INCLUDE_FILTER);
    }
  };

  const [cursor, setCursor] = useState(null);
  const [inclusionFiltersCursor, setInclusionFiltersCursor] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedInclusionFilterType, setSelectedInclusionFilterType] =
    useState(INCLUDE_FILTER);
  const [tagsById, setTagsById] = useState(recursiveTagIndex(tagCategory));
  const [isFullText, setIsFullText] = useState(true);
  const [fullTextPrompt, setFullTextPrompt] = useState('Show labels only');
  const [showFullTextToggle, setShowFullTextToggle] = useState(
    recursiveTagDescDelta(tagCategory),
  );
  const [isDirty, setIsDirty] = useState(null);
  const firstLevel = useRef({});

  useEffect(() => {
    let inclusionFiltersCursor;
    if (enableInclusionFilters) {
      inclusionFiltersCursor = filterCursor.sub(
        ['inclusionFilters'],
        [],
        (newData) => updateInclusionFilterType(newData),
      );
      setInclusionFiltersCursor(inclusionFiltersCursor);
    }
    const path = enableInclusionFilters
      ? ['tags', tagCategoryId]
      : [tagCategoryId];
    const cursor = filterCursor.sub(path, [], (newData) =>
      updateCursor(newData),
    );
    setCursor(cursor);
    let selectedFilters;
    if (
      flattenHierarchy.current.filtersByCategory &&
      flattenHierarchy.current.filtersByCategory[tagCategoryId]
    ) {
      selectedFilters =
        flattenHierarchy.current.filtersByCategory[tagCategoryId];
      if (inclusionFiltersCursor) {
        inclusionFiltersCursor.requestChange(inclusionFiltersCursor.value);
      }
      cursor.requestChange(selectedFilters);
    } else {
      selectedFilters = cursor.value || [];
    }

    setSelectedFilters(selectedFilters);

    firstLevel.current = determineFirstLevel(tagCategory);
    if (!flattenHierarchy.current.tree[tagCategoryId]) {
      flattenHierarchy.current.tree[tagCategoryId] = firstLevel.current;

      flatHierarchy(
        firstLevel.current.tags,
        flattenHierarchy.current,
        tagCategoryId,
      );
    }
    /**
     * Backwards compatible
     * */
    preToggleFilters(selectedFilters, flattenHierarchy.current, tagsById);
  }, []);

  useEffect(() => {
    if (inclusionFiltersCursor) {
      inclusionFiltersCursor.requestChange({
        excludeHigher: excludeHigherIds,
        includeOnly: includeOnlyIds,
      });
    }
  }, [excludeHigherIds, includeOnlyIds]);

  useEffect(() => {
    /**
     * Reset inner cursor - support for "reset filter"
     * */
    const emptyCondition = enableInclusionFilters
      ? _.isEmpty(filterCursor.value.tags)
      : _.isEmpty(filterCursor.value);

    if (cursor && emptyCondition) {
      const { tags } = firstLevel.current;
      setTagSelections('deselect', tags, flattenHierarchy.current, tagsById);
      cursor.requestChange([]);
    }
  }, [filterCursor.value.tags]);

  const isTagSelected = (tag, selected) => {
    return (
      tag.id === selected ||
      (tag.tags
        ? tag.tags?.find((subtag) => isTagSelected(subtag, selected))
        : false)
    );
  };

  useEffect(() => {
    if (
      !selectedFilters.every((selectedFilter) =>
        tagCategory.tags?.find((tag) => isTagSelected(tag, selectedFilter)),
      )
    ) {
      const { tags } = firstLevel.current;
      setTagSelections('deselect', tags, flattenHierarchy.current, tagsById);

      cursor.requestChange([]);
    }
  }, [tagCategory.tags.toString(), selectedFilters]);

  useEffect(() => {
    firstLevel.current = determineFirstLevel(tagCategory);
    setTagsById(recursiveTagIndex(tagCategory));
    setShowFullTextToggle(recursiveTagDescDelta(tagCategory));
  }, [tagCategory]);

  const selectAll = () => {
    const { tags } = firstLevel.current;
    setIsDirty(true);
    setTagSelections('select', tags, flattenHierarchy.current, tagsById);

    const tagIDs = _.chain(tags).map(getAllTagIds).flatten().value();
    cursor.requestChange(tagIDs);
  };

  /**
   * Clear multiple click handler
   * */
  const clearMultiple = () => {
    const { tags } = firstLevel.current;
    setTagSelections('deselect', tags, flattenHierarchy.current, tagsById);

    cursor.requestChange([]);
    if (enableInclusionFilters) {
      const updatedTags = _.omit(filterCursor.value.tags, tagCategoryId);
      const excludeHigher =
        filterCursor.value.inclusionFilters.excludeHigher.filter(
          (id) => id !== tagCategoryId,
        );
      const includeOnly =
        filterCursor.value.inclusionFilters.includeOnly.filter(
          (id) => id !== tagCategoryId,
        );
      onFilterChange(updatedTags, excludeHigher);
      filterCursor.requestChange(
        {
          tags: updatedTags,
          inclusionFilters: {
            excludeHigher,
            includeOnly,
          },
        },
        flattenHierarchy.current,
      );
      inclusionFiltersCursor.requestChange({
        excludeHigher:
          inclusionFiltersCursor.value.inclusionFilters.excludeHigher.filter(
            (id) => id !== tagCategoryId,
          ),
        includeOnly: inclusionFiltersCursor.value.inclusionFilters.includeOnly,
      });
    } else {
      const updated = _.omit(filterCursor.value, tagCategoryId);
      onFilterChange(updated);
      filterCursor.requestChange(updated, flattenHierarchy.current);
    }
  };

  const addTag = (tag, categoryId, selectType = 'multiple') => {
    // Overriding management functions can come from above, e.g. TagFilters.
    // This is because tags are nuts.

    const { id: tagId, parent_id: parentId } = tag;

    let updated;
    if (selectType === 'single') {
      if (eliminateTags) {
        eliminateTags(selectedFilters);
      }
      updated = [tagId];
    } else {
      updated = _([tagId, ...selectedFilters])
        .compact()
        .uniq()
        .value();
    }

    setIsDirty(true);
    selectTag(tagId, parentId, flattenHierarchy.current, tagsById);
    cursor.requestChange(updated);
  };

  /*
   * This function adds recursively the sub-children of selected tag
   */
  const addTagAndChildren = (tag, selectType = 'multiple') => {
    // The end nodes don't have a presence in the `flattenHierarchy`, for reasons unknown
    // But since they're leafs, they don't have to sub select any other tag, so we're fine using just their id
    const childrenIds = _.isObject(flattenHierarchy.current[tag.id])
      ? flattenHierarchy.current[tag.id].childrenIds
      : [tag.id];

    let updated;
    if (selectType === 'single') {
      if (eliminateTags) {
        eliminateTags(selectedFilters);
      }
      updated = childrenIds;
    } else {
      updated = _([...childrenIds, ...selectedFilters])
        .compact()
        .uniq()
        .value();
    }

    const updateHierarchy = (innerTag) => {
      selectTag(
        innerTag.id,
        innerTag.parent_id,
        flattenHierarchy.current,
        tagsById,
      );
      (innerTag.tags || []).map(updateHierarchy);
    };

    setIsDirty(true);
    updateHierarchy(tag);
    cursor.requestChange(updated);
  };

  const unselectTag = (tag) => {
    const { id: tagId, parent_id: parentId } = tag;

    setIsDirty(true);
    deselectTag(tagId, parentId, flattenHierarchy.current, tagsById);

    if (enableInclusionFilters) {
      if (Array.isArray(cursor.value)) {
        cursor.requestChange(_.without(cursor.value, tagId));
      } else {
        cursor.requestChange(
          _.without(cursor.value.tags[tagCategoryId], tagId),
        );
      }
    } else {
      cursor.requestChange(_.without(cursor.value[tagCategoryId], tagId));
    }
  };

  const unselectTagWithChildrenAndParents = (tag) => {
    const unselectedTags = [tag.id];

    const collectChildrenIds = (tagId) => {
      // Leaf nodes aren't present in the `flattenHierarchy`, so we need to check for that
      if (_.isObject(flattenHierarchy.current[tagId])) {
        // There may be a content issue when a tag ID is listed as a child of itself. We need to remove it from the list
        // of children IDs to avoid an infinite recursion.
        const childrenIds = _.without(
          flattenHierarchy.current[tagId].childrenIds,
          tagId,
        );
        unselectedTags.push(...childrenIds);
        childrenIds.forEach((childId) => collectChildrenIds(childId));
      }
    };

    const collectParentIds = (parentId) => {
      if (parentId) {
        unselectedTags.push(parentId);
        if (_.isObject(flattenHierarchy.current[parentId])) {
          collectParentIds(flattenHierarchy.current[parentId].parent_id);
        }
      }
    };

    collectChildrenIds(tag.id);
    collectParentIds(tag.parent_id);

    const updateHierarchy = (innerTag) => {
      deselectTag(
        innerTag.id,
        innerTag.parent_id,
        flattenHierarchy.current,
        tagsById,
      );
      (innerTag.tags || []).map(updateHierarchy);
    };

    setIsDirty(true);
    updateHierarchy(tag);

    if (enableInclusionFilters) {
      if (Array.isArray(cursor.value)) {
        cursor.requestChange(_.difference(cursor.value, unselectedTags));
      } else {
        cursor.requestChange(
          _.difference(cursor.value.tags[tagCategoryId], unselectedTags),
        );
      }
    } else {
      cursor.requestChange(
        _.difference(cursor.value[tagCategoryId], unselectedTags),
      );
    }
  };

  const removeTag = (tag) => {
    const { id: tagId, parent_id: parentId } = tag;

    if (eliminateTags) {
      eliminateTags([tagId]);
    }

    cursor.requestChange([]);
    deselectTag(tagId, parentId, flattenHierarchy.current, tagsById);

    if (enableInclusionFilters) {
      const { tags, inclusionFilters } = filterCursor.value;
      const updatedTags = _.omit(tags, tagCategoryId);
      const excludeHigher = inclusionFilters.excludeHigher.filter(
        (id) => id !== tagCategoryId,
      );
      const includeOnly = inclusionFilters.includeOnly.filter(
        (id) => id !== tagCategoryId,
      );
      onFilterChange(updatedTags, excludeHigher);
      filterCursor.requestChange(
        {
          tags: updatedTags,
          inclusionFilters: {
            excludeHigher,
            includeOnly,
          },
        },
        flattenHierarchy.current,
      );
    } else {
      const updated = _.omit(filterCursor.value, tagCategoryId);
      onFilterChange(updated);
      filterCursor.requestChange(updated, flattenHierarchy.current);
    }
  };

  const toggleFullText = () => {
    if (isFullText) {
      setFullTextPrompt('Show full text');
      setIsFullText(false);
    } else {
      setFullTextPrompt('Show labels only');
      setIsFullText(true);
    }
  };

  const hasTwoLevelNodes = useMemo(
    () =>
      (firstLevel.current.tags || []).some((tag) =>
        (tag.tags || []).some(
          (secondLevel) => (secondLevel.tags || []).length > 0,
        ),
      ),
    [firstLevel.current],
  );
  const nodesRef = useRef();
  const [nodesCollapsed, setNodesCollapsed] = useState(
    !enableHierarchicalSelection,
  );
  const onToggleCollapsed = () => setNodesCollapsed((prev) => !prev);

  useLayoutEffect(() => {
    // Rerender MathJax if children of node were opened
    !nodesCollapsed && nodesRef.current && setMathJaxTypeset(nodesRef.current);
  }, [nodesCollapsed]);

  const onInclusionFilterChange = (newInclusionFilterType) => {
    if (!isDirty) {
      setIsDirty(true);
    }

    if (newInclusionFilterType === INCLUDE_ONLY_FILTER) {
      inclusionFiltersCursor.requestChange({
        excludeHigher:
          inclusionFiltersCursor.value.inclusionFilters.excludeHigher.filter(
            (id) => id !== tagCategoryId,
          ),
        includeOnly: [
          ...inclusionFiltersCursor.value.inclusionFilters.includeOnly,
          tagCategoryId,
        ],
      });
    } else if (newInclusionFilterType === EXCLUDE_HIGHER_FILTER) {
      inclusionFiltersCursor.requestChange({
        excludeHigher: [
          ...inclusionFiltersCursor.value.inclusionFilters.excludeHigher,
          tagCategoryId,
        ],
        includeOnly:
          inclusionFiltersCursor.value.inclusionFilters.includeOnly.filter(
            (id) => id !== tagCategoryId,
          ),
      });
    } else {
      inclusionFiltersCursor.requestChange({
        excludeHigher:
          inclusionFiltersCursor.value.inclusionFilters.excludeHigher.filter(
            (id) => id !== tagCategoryId,
          ),
        includeOnly:
          inclusionFiltersCursor.value.inclusionFilters.includeOnly.filter(
            (id) => id !== tagCategoryId,
          ),
      });
    }
  };

  const onPopOverClick = (isOpen) => {
    if (!isOpen && isDirty) {
      setIsDirty(false);
      if (enableInclusionFilters) {
        const filters = {
          ...filterCursor.value.tags,
          [tagCategoryId]: selectedFilters,
        };
        const { inclusionFilters } = inclusionFiltersCursor.value;
        onFilterChange(filters, inclusionFilters.excludeHigher);
        filterCursor.requestChange(
          {
            tags: filters,
            inclusionFilters,
          },
          !!flattenHierarchy.current,
        );
      } else {
        const filters = {
          ...filterCursor.value,
          [tagCategoryId]: selectedFilters,
        };
        onFilterChange(filters);
        filterCursor.requestChange(filters, flattenHierarchy.current);
      }
    }
  };

  let tagSelected = null;
  if (selectedFilters.length === 1) {
    const tag = tagsById?.[selectedFilters?.[0]];
    if (tag) {
      tagSelected = (
        <Tag onClick={() => removeTag(tag)} key={tag.id} disabled={isDisabled}>
          {tag.label}
        </Tag>
      );
    }
  }

  if (selectedFilters.length > 1) {
    tagSelected = (
      <Tag onClick={clearMultiple} disabled={isDisabled}>
        Multiple Selected
      </Tag>
    );
  }

  const otherProps = {
    selectedFilters,
    tagsById,
    isFullText,
    fullTextPrompt,
    showFullTextToggle,
  };

  const filterDetails = FILTER_DETAILS.find(
    (x) => x.name === tagCategory.description,
  );
  const details =
    filterDetails && filterDetails.details(showNewExamAlignmentText);

  return (
    <div data-test-filter-button-wrapper className={className}>
      <div className="relative flex w-full">
        {!!details && (
          <div
            className="absolute right-0 z-50 cursor-pointer tag-tree__details"
            style={{
              transform: `translate(-25px, 5px)`,
            }}
          >
            <DetailsPopover
              popOverClassName="ml-auto"
              details={details}
              tagCategoryDescription={tagCategory.description}
            />
          </div>
        )}
        <ListContainerControls
          onToggle={onPopOverClick}
          tagCategoryDescription={tagCategory.description}
          toggleFilters={false}
          inlineFilters={inlineFilters}
          onContentMount={(contentElementRef) =>
            setMathJaxTypeset(contentElementRef.current)
          }
          disabled={isDisabled}
          className="tag-tree-selector w-full"
          isNarrow
          buttonClassName="w-full"
          showNewExamAlignmentText={showNewExamAlignmentText}
          size="medium"
          trigger={
            <ButtonWithIcon
              isOutline
              background="white"
              className="w-full justify-between"
              containerClassName="w-full items-center"
              icon="arrow_drop_down"
              iconOrientation="right"
              data-tracking-id={`tag-tree-selector-${_.kebabCase(
                tagCategory.label,
              )}`}
            >
              {tagCategory.label}
            </ButtonWithIcon>
          }
        >
          <div className="border-left border-right relative">
            <div className="sticky bg-white top-0 z-10">
              <div
                className={cn(
                  'flex items-center gap-x-3.5 h-25 justify-center',
                  {
                    'border-bottom': enableInclusionFilters,
                  },
                )}
              >
                {selectType === 'multiple' ? (
                  <Button
                    isSecondary
                    onClick={selectAll}
                    accessibility={{
                      'aria-label': `${tagCategory.label} select all`,
                    }}
                    data-test-filter-select-all
                  >
                    Select All
                  </Button>
                ) : null}
                <Button
                  isSecondary
                  onClick={clearMultiple}
                  accessibility={{
                    'aria-label': `${tagCategory.label} clear`,
                  }}
                  data-test-filter-clear-selected
                >
                  Clear
                </Button>
                {showFullTextToggle && isFullTextToggleAvailable && (
                  <Button
                    isSecondary
                    onClick={toggleFullText}
                    accessibility={{
                      'aria-label': `${tagCategory.label} ${fullTextPrompt}`,
                    }}
                  >
                    {fullTextPrompt}
                  </Button>
                )}
                {isCollapsible && hasTwoLevelNodes && (
                  <Button
                    isSecondary
                    onClick={onToggleCollapsed}
                    accessibility={{
                      'aria-label': `${tagCategory.label} ${
                        nodesCollapsed ? 'Expand' : 'Collapse'
                      }`,
                    }}
                  >
                    {nodesCollapsed ? 'Expand' : 'Collapse'}
                  </Button>
                )}
              </div>
              {enableInclusionFilters && (
                <InclusionFilters
                  selectedType={selectedInclusionFilterType}
                  onChange={onInclusionFilterChange}
                  hasExcludeHigherFilter={hasExcludeHigherFilter}
                  tagCategoryDescription={tagCategory.description}
                />
              )}
            </div>
            <ul
              ref={nodesRef}
              className="p-0 m-0 list-none divide-y divide-solid divide-gray-400"
            >
              {firstLevel.current &&
                (firstLevel.current.tags || []).map((t) => (
                  <Node
                    key={t.id}
                    tag={t}
                    addTag={addTag}
                    addTagAndChildren={addTagAndChildren}
                    unselectTag={unselectTag}
                    unselectTagWithChildrenAndParents={
                      unselectTagWithChildrenAndParents
                    }
                    {...props}
                    {...otherProps}
                    level={1}
                    collapsed={nodesCollapsed}
                    containerClassName="py-4.4 px-6"
                    classNameLabel="text-base text-black font-medium"
                  />
                ))}
            </ul>
          </div>
        </ListContainerControls>
      </div>
      {tagSelected}
    </div>
  );
};

TagTreeSelector.displayName = 'TagTreeSelector';

TagTreeSelector.propTypes = {
  filterCursor: PropTypes.shape({
    requestChange: PropTypes.func,
    sub: PropTypes.func,
    value: PropTypes.object,
  }),
  tagCategory: PropTypes.object,
  selectType: PropTypes.oneOf(['single', 'multiple']),
  showAllTags: PropTypes.bool,
  isFullTextToggleAvailable: PropTypes.bool,
  eliminateTags: PropTypes.func,
  isDisabled: PropTypes.bool,
  onPopOverClick: PropTypes.func,
  location: PropTypes.object,
  onFilterChange: PropTypes.func,
  isCollapsible: PropTypes.bool,
  enableHierarchicalSelection: PropTypes.bool,
};

TagTreeSelector.defaultProps = {
  isFullTextToggleAvailable: true,
  selectType: 'multiple',
  isDisabled: false,
  onPopOverClick: () => {},
  location: {},
  onFilterChange: () => {},
  isCollapsible: false,
  enableHierarchicalSelection: false,
};

const mapStateToProps = (state) => ({
  showAttestationFlag: state.featureFlag.show_attestation_confirmation_dialog,
  subjectId: Number.parseInt(state.subject.data.id, 10),
  showNewExamAlignmentText:
    state.featureFlag.fy_13178_show_new_exam_alignment_text,
});

export default connect(mapStateToProps)(TagTreeSelector);
